// B-5 codified ppq math helpers — JS mirror of `jobq-api/assembly_units.py`.
//
// Background
// ==========
// Assemblies have a `primary_part_quantity` (ppq) — the number of physical
// primary-part pieces required to build ONE finished assembly. For example,
// a Hy-Flo Tank Stand has ppq=4 (each stand needs 4 legs).
//
// The May 4 2026 schema fold committed JobQ to a single accounting
// convention: **the primary part's quantity is stored in primary-part-space**,
// i.e. `quantity = N_assemblies * ppq`. This means:
//   * `sales_order_lines.quantity_ordered` for the primary line = N * ppq
//   * `job_parts.quantity` for the primary part = N * ppq
//   * `planned_box_zones.planned_qty` (for assembly primaries) = N * ppq
//   * `shipment_pallet_items.quantity` (for assembly primaries) = N * ppq
//   * `job_tasks.qty_completed` for the primary's Shipping task = N * ppq
//
// Component parts and consumables, by contrast, are stored in component
// space (per-assembly multiplier × N).
//
// Use these helpers when converting between the two spaces so the intent
// is explicit at every call site and the COALESCE(ppq, 1) defensive guard
// is centralized.
//
// All helpers return integers (Math.floor / Math.trunc semantics).

/** Internal: normalize ppq. null/undefined/0/negative → 1 (matches SQL
 * `COALESCE(a.primary_part_quantity, 1)` pattern). */
function _coercePpq(ppq) {
  if (ppq === null || ppq === undefined || ppq <= 0) return 1
  return parseInt(ppq, 10) || 1
}

/**
 * Convert a primary-part-space quantity back to assembly count N.
 *
 * Math: floor(primary_qty / ppq) — with ppq normalized via _coercePpq.
 *
 * Use this when you have the primary part's quantity_ordered /
 * job_parts.quantity / spi.quantity / qty_completed (all stored in
 * primary-part-space = N × ppq) and you want the human-readable
 * "how many finished assemblies" count for display.
 *
 * @example
 *   // SO line for primary FAB308616, quantity_ordered=40, assembly
 *   // ASY311787 with ppq=4 → 10 finished ASY311787 assemblies.
 *   assemblyCountFromPrimarySpace(40, 4) === 10
 */
export function assemblyCountFromPrimarySpace(primaryQty, ppq) {
  const q = parseInt(primaryQty, 10) || 0
  return Math.floor(q / _coercePpq(ppq))
}

/**
 * Convert an assembly count N into primary-part-space quantity.
 *
 * Math: n_assemblies × ppq.
 *
 * Use this on write paths when persisting quantity_ordered /
 * job_parts.quantity / spi.quantity for an assembly's primary part —
 * the stored value should always be N × ppq.
 *
 * @example
 *   primarySpaceFromAssemblyCount(10, 4) === 40  // 10 stands × 4 legs
 */
export function primarySpaceFromAssemblyCount(nAssemblies, ppq) {
  return (parseInt(nAssemblies, 10) || 0) * _coercePpq(ppq)
}

/**
 * Component pieces produced/needed for an assembly run, given the
 * primary's quantity in primary-part-space.
 *
 * Math: floor(primary_qty / ppq) × qpa — first recover the assembly
 * count N, then multiply by the per-assembly multiplier.
 *
 * Use this when computing how many physical pieces of a component
 * are needed (or have been produced) for a given assembly run.
 *
 * @example
 *   // 10 ASY311787 stands (ppq=4 → primary_qty=40) need 2 DUNNAGEH3H4
 *   // per stand → 20 DUNNAGEH3H4 pieces total.
 *   componentPiecesForAssemblyRun(40, 4, 2) === 20
 */
export function componentPiecesForAssemblyRun(primaryQty, ppq, qpa) {
  return assemblyCountFromPrimarySpace(primaryQty, ppq) * (parseInt(qpa, 10) || 0)
}

// =============================================================================
// Station-rank helpers — mirror jobq-api station_order + fab_sub_processes
// (the same ranking build_single_part_report_page uses for is_post_join).
// Fabrication sub-processes (Welding, Cutting, …) rank as Fabrication so a
// "Welding" join compares correctly against a Fabrication-station task.
// =============================================================================

const _STATION_RANK = { Laser: 1, Tap: 2, Brake: 3, Fabrication: 4, Blast: 5, Coating: 5.5, Finish: 6, Shipping: 7 }
const _FAB_SUB = new Set(['Cutting', 'Drilling', 'Milling', 'Lathe', 'Welding', 'Tapping', 'Assembly'])

function _stationRank(station) {
  if (!station) return 999
  if (_FAB_SUB.has(station)) return _STATION_RANK.Fabrication
  return _STATION_RANK[station] ?? 999
}

/**
 * True when `currentStation` is at or after the assembly's join station — i.e.
 * the loose primary parts have been joined into finished assemblies, so the
 * display should switch from plate-count to assembly-count. Mirrors the
 * backend is_post_join (current_station_rank >= join_station_rank).
 */
export function isAtOrAfterJoin(currentStation, joinStation) {
  if (!joinStation) return false
  return _stationRank(currentStation) >= _stationRank(joinStation)
}

/**
 * Display quantity for a job-queue row. For an assembly primary at/after the
 * join station it returns the assembly count (rawQty / ppq); otherwise rawQty
 * is returned unchanged (pre-join plate count, or non-assembly parts).
 * `currentStation` is the queue's workstation; Fabrication rows are compared by
 * their sub_process (e.g. Welding).
 *
 * @example queueRowAssemblyQty(40, {assembly_id:102, primary_part_quantity:4,
 *   join_workstation:'Welding', sub_process:'Welding'}, 'Fabrication') === 10
 */
export function queueRowAssemblyQty(rawQty, row, currentStation) {
  if (!row || !row.assembly_id || !(row.primary_part_quantity > 1)) return rawQty
  // A SET row's quantity is ALREADY an assembly count: the scheduler inserts
  // `assemblies_for_this_set` on the split-set INSERT, not a primary-part count.
  // Dividing it again showed "Task Qty: 0" on every card of job 9403's ten-set
  // weld — the same defect the printed report had as "Assembly Qty: 0".
  if (isAssemblySetRow(row)) return rawQty
  // Name-aware, not rank-aware: a part-level Cutting task on an assembly primary with
  // ppq>1 would otherwise have its count divided as though the rack were already welded.
  if (!isRowAtOrAfterJoin(row, currentStation)) return rawQty
  return assemblyCountFromPrimarySpace(rawQty, row.primary_part_quantity)
}

/** True when a queue row is one SET of an assembly build (quantity already in
 * assembly-count space). `set_identifier` reaches the UI from /queue/{workstation}. */
export function isAssemblySetRow(row) {
  return !!row && row.set_identifier !== null && row.set_identifier !== undefined
}

/** The station a row should be ranked at: Fabrication rows rank by their sub_process
 * (Welding, Cutting, …) since Fabrication itself is not specific enough to compare
 * against a join station. */
export function rowEffectiveStation(row, currentStation) {
  return (currentStation === 'Fabrication' && row && row.sub_process) ? row.sub_process : currentStation
}

/**
 * Is this row AT or AFTER its assembly's join station?
 *
 * ⚠ `isAtOrAfterJoin` alone cannot answer this for a Fabrication row. Every fab
 * sub-process shares rank 4, so `isAtOrAfterJoin('Cutting', 'Welding')` is TRUE —
 * a saw-cutting card would be treated as an assembled rack. Within Fabrication the
 * comparison has to be by NAME; rank is only meaningful ACROSS stations. Same
 * constraint the API hit computing readiness.
 *
 * Consequence, deliberate: a fab sub-process that genuinely follows the join (a
 * separate 'Assembly' step after a 'Welding' join) reads as pre-join here, because
 * nothing in a queue row carries the order of fab steps. Under-claiming is the safe
 * direction — it labels a real assembly as a part, never a loose part as an assembly.
 */
export function isRowAtOrAfterJoin(row, currentStation) {
  if (!row || !row.join_workstation) return false
  const eff = rowEffectiveStation(row, currentStation)
  if (!eff) return false
  if (_FAB_SUB.has(eff) && _FAB_SUB.has(row.join_workstation)) return eff === row.join_workstation
  return isAtOrAfterJoin(eff, row.join_workstation)
}

/**
 * True when a queue row should be presented as an ASSEMBLY rather than a loose part —
 * it belongs to an assembly and sits at or after the join, so what the operator has in
 * front of them is the assembled product, not the primary piece.
 *
 * Drives the "Assembly:" vs "Part:" label and which name is shown.
 */
export function isAssemblyRow(row, currentStation) {
  if (!row || !row.assembly_id) return false
  return isRowAtOrAfterJoin(row, currentStation)
}

/**
 * How many finished assemblies THIS ONE TASK covers.
 *
 * Two different spaces reach this function and only `set_identifier` tells them
 * apart:
 *   * SET task     — the scheduler writes `assemblies_for_this_set` straight into
 *                    job_tasks.quantity, so it is ALREADY an assembly count.
 *   * whole-job task — quantity is in primary-part-space (N × ppq) like every
 *                    other primary-part quantity, so it has to be divided.
 * Dividing a SET quantity a second time is the defect that printed "Task Qty: 0"
 * on job 9403 and "Assemblies Ordered: 0" on job 9537.
 *
 * @example
 *   // job 9537: weld SET 1 of 2, quantity=1, ppq=1 → this task builds 1 assembly
 *   assembliesOnTask({ quantity: 1, set_identifier: 1 }, 1) === 1
 *   // a whole-job weld task for 10 stands, ppq=4 → quantity is 40 legs
 *   assembliesOnTask({ quantity: 40, set_identifier: null }, 4) === 10
 */
export function assembliesOnTask(task, ppq) {
  if (!task) return 0
  const q = parseInt(task.quantity, 10) || 0
  if (isAssemblySetRow(task)) return q
  return assemblyCountFromPrimarySpace(q, ppq)
}

/**
 * Component pieces THIS ONE TASK needs — the number the operator standing at the
 * card has to physically collect.
 *
 * Math: assembliesOnTask(task, ppq) × qpa.
 *
 * Distinct from the BOM's `job_qty`, which is qpa × the WHOLE JOB's assembly
 * count. On job 9537 the welder on SET 1 of 2 needs 6 of 352-2581; the job needs
 * 12. Showing only the job figure on a set card reads as though one assembly took
 * twice the parts it does.
 *
 * @example
 *   // job 9537 SET 1: 6 × 352-2581 per assembly, 1 assembly in this set
 *   taskPiecesForComponent({ quantity: 1, set_identifier: 1 }, 1, 6) === 6
 */
export function taskPiecesForComponent(task, ppq, qpa) {
  return assembliesOnTask(task, ppq) * (parseInt(qpa, 10) || 0)
}

// =============================================================================
// Inline self-tests — fire on import in development; harmless in production.
// =============================================================================

;(function selfTest() {
  // Skip in production builds for marginal bundle-size savings.
  if (typeof process !== 'undefined' && process.env && process.env.NODE_ENV === 'production') {
    return
  }
  // assemblyCountFromPrimarySpace
  if (assemblyCountFromPrimarySpace(40, 4) !== 10) throw new Error('assemblyCount: 10 stands ppq=4')
  if (assemblyCountFromPrimarySpace(250, 1) !== 250) throw new Error('assemblyCount: ppq=1 passthrough')
  if (assemblyCountFromPrimarySpace(40, null) !== 40) throw new Error('assemblyCount: null ppq → 1')
  if (assemblyCountFromPrimarySpace(40, 0) !== 40) throw new Error('assemblyCount: 0 ppq → 1')
  if (assemblyCountFromPrimarySpace(0, 4) !== 0) throw new Error('assemblyCount: 0 primary')
  if (assemblyCountFromPrimarySpace(null, 4) !== 0) throw new Error('assemblyCount: null primary → 0')
  if (assemblyCountFromPrimarySpace(42, 4) !== 10) throw new Error('assemblyCount: floor-divide')

  // primarySpaceFromAssemblyCount
  if (primarySpaceFromAssemblyCount(10, 4) !== 40) throw new Error('primarySpace: 10×4')
  if (primarySpaceFromAssemblyCount(250, 1) !== 250) throw new Error('primarySpace: ppq=1')
  if (primarySpaceFromAssemblyCount(10, null) !== 10) throw new Error('primarySpace: null ppq → 1')
  if (primarySpaceFromAssemblyCount(0, 4) !== 0) throw new Error('primarySpace: 0 assemblies')

  // componentPiecesForAssemblyRun
  if (componentPiecesForAssemblyRun(40, 4, 1) !== 10) throw new Error('component: SO-1029 DUNNAGEH1')
  if (componentPiecesForAssemblyRun(40, 4, 2) !== 20) throw new Error('component: SO-1029 DUNNAGEH3H4')
  if (componentPiecesForAssemblyRun(250, 1, 1) !== 250) throw new Error('component: SO-1020 CRIBSTOPPER')
  if (componentPiecesForAssemblyRun(40, 4, 0) !== 0) throw new Error('component: qpa=0 → 0')
  if (componentPiecesForAssemblyRun(40, 4, null) !== 0) throw new Error('component: null qpa → 0')

  // isAtOrAfterJoin
  if (isAtOrAfterJoin('Welding', 'Welding') !== true) throw new Error('join: Welding>=Welding')
  if (isAtOrAfterJoin('Fabrication', 'Welding') !== true) throw new Error('join: Fabrication>=Welding (fab-sub)')
  if (isAtOrAfterJoin('Shipping', 'Welding') !== true) throw new Error('join: Shipping>=Welding')
  if (isAtOrAfterJoin('Laser', 'Welding') !== false) throw new Error('join: Laser<Welding')
  if (isAtOrAfterJoin('Brake', null) !== false) throw new Error('join: no join station → false')

  // queueRowAssemblyQty — FAB308616/ASY311787-LUG (ppq=4), join at Welding
  const _asmRow = { assembly_id: 102, primary_part_quantity: 4, join_workstation: 'Welding', sub_process: 'Welding' }
  if (queueRowAssemblyQty(40, _asmRow, 'Fabrication') !== 10) throw new Error('queueQty: post-join 40→10')
  if (queueRowAssemblyQty(40, { ..._asmRow, sub_process: null }, 'Laser') !== 40) throw new Error('queueQty: pre-join stays 40')
  if (queueRowAssemblyQty(40, { ..._asmRow, sub_process: 'Cutting' }, 'Fabrication') !== 40) throw new Error('queueQty: Cutting is pre-join, must NOT divide')
  if (queueRowAssemblyQty(40, { primary_part_quantity: 1 }, 'Shipping') !== 40) throw new Error('queueQty: non-assembly passthrough')

  // SET rows — job 9403 shape: ten weld sets of 1 assembly each, ppq=3. Dividing
  // again is what produced "Task Qty: 0" on every card.
  const _setRow = { assembly_id: 122, primary_part_quantity: 3, join_workstation: 'Welding',
                    sub_process: 'Welding', set_identifier: 1 }
  if (queueRowAssemblyQty(1, _setRow, 'Fabrication') !== 1) throw new Error('queueQty: SET row 1 stays 1')
  if (queueRowAssemblyQty(4, { ..._setRow, set_identifier: 7 }, 'Fabrication') !== 4) throw new Error('queueQty: SET row 4 stays 4')
  if (queueRowAssemblyQty(30, { ..._setRow, set_identifier: null }, 'Fabrication') !== 10) throw new Error('queueQty: non-set still divides')
  if (isAssemblySetRow(_setRow) !== true) throw new Error('setRow: identifier present')
  if (isAssemblySetRow({ ..._setRow, set_identifier: null }) !== false) throw new Error('setRow: null')
  if (isAssemblySetRow({ ..._setRow, set_identifier: 0 }) !== true) throw new Error('setRow: 0 is a real identifier')

  // isAssemblyRow — drives the "Assembly:" vs "Part:" label
  if (isAssemblyRow(_setRow, 'Fabrication') !== true) throw new Error('asmRow: at join → assembly')
  if (isAssemblyRow({ ..._setRow, sub_process: 'Cutting' }, 'Fabrication') !== false) throw new Error('asmRow: Cutting is pre-join → part')
  if (isAssemblyRow({ ..._setRow, sub_process: null }, 'Shipping') !== true) throw new Error('asmRow: Shipping is after join')
  if (isAssemblyRow({ ..._setRow, sub_process: null }, 'Laser') !== false) throw new Error('asmRow: Laser is pre-join')
  if (isAssemblyRow({ assembly_id: null }, 'Shipping') !== false) throw new Error('asmRow: non-assembly')
  if (isAssemblyRow({ assembly_id: 1, join_workstation: null }, 'Shipping') !== false) throw new Error('asmRow: no join station')
  // ppq=1 assemblies are STILL assemblies for labelling purposes, even though their
  // quantity needs no conversion.
  if (isAssemblyRow({ assembly_id: 70, primary_part_quantity: 1, join_workstation: 'Welding',
                      sub_process: 'Welding' }, 'Fabrication') !== true) throw new Error('asmRow: ppq=1 is still an assembly')
  // assembliesOnTask / taskPiecesForComponent — job 9537 shape: 526-3105 SUPPORT AS,
  // ppq=1, 2 ordered, split into two weld sets of one assembly each.
  const _wSet1 = { quantity: 1, set_identifier: 1 }
  if (assembliesOnTask(_wSet1, 1) !== 1) throw new Error('onTask: SET of 1 stays 1')
  if (assembliesOnTask({ quantity: 2, set_identifier: null }, 1) !== 2) throw new Error('onTask: whole job ppq=1')
  if (assembliesOnTask({ quantity: 40, set_identifier: null }, 4) !== 10) throw new Error('onTask: whole job ppq=4 divides')
  if (assembliesOnTask({ quantity: 4, set_identifier: 3 }, 4) !== 4) throw new Error('onTask: SET must NOT divide by ppq')
  if (assembliesOnTask(null, 4) !== 0) throw new Error('onTask: no task → 0')
  if (assembliesOnTask({ quantity: undefined, set_identifier: 1 }, 1) !== 0) throw new Error('onTask: undefined qty → 0')
  // The four rows of job 9537's Sub-Components table, per SET (job totals are 2/12/4/2).
  if (taskPiecesForComponent(_wSet1, 1, 1) !== 1) throw new Error('pieces: 352-2579 ×1')
  if (taskPiecesForComponent(_wSet1, 1, 6) !== 6) throw new Error('pieces: 352-2581 ×6')
  if (taskPiecesForComponent(_wSet1, 1, 2) !== 2) throw new Error('pieces: 352-2582 ×2')
  if (taskPiecesForComponent(_wSet1, 1, 1) !== 1) throw new Error('pieces: 526-3104 ×1')
  // Un-split job: per-task and per-job agree, which is why the column is safe to
  // show on every assembly card, not only on set cards.
  if (taskPiecesForComponent({ quantity: 2, set_identifier: null }, 1, 6) !== 12) throw new Error('pieces: whole job = job_qty')
  if (taskPiecesForComponent(_wSet1, 1, null) !== 0) throw new Error('pieces: null qpa → 0')
})()
